// ジェスチャの振り分け（v0.7.3 でフリック判定に作り替え）。純関数。
//
// 庭の空きスペースをドラッグする操作は **常に「島内回転」**（横移動を Y 回転にライブ反映）
// とし、ドラッグ中に島送りはしない。回転も島送りもどちらも横方向なので「方向」では区別
// できず、v0.7.2 までの「横優位なら即ページ」方式は、島をゆっくり眺め回したいだけの横
// ドラッグを島送りに倒してしまっていた（敏感すぎた）。
//
// v0.7.3 の方針: 島送りは **指を離す瞬間の水平速度（フリック）** で判定する。
//   - ゆっくり持続的な横ドラッグ（眺め回し）= いくら横に動いても回転だけ（絶対にページしない）。
//   - 素早い横フリック（指の弾き）= そのとき初めて 1 島だけ送る。
//
// pointerup の直前数サンプルの (座標, 時刻) から離す瞬間の水平速度 vx を出し、
//   |vx| >= FLICK_VX(px/ms) かつ 水平優位（|dx| > |dy|*H_DOM）かつ 合計 |dx| >= FLICK_MIN_DX
// のときだけ「フリック＝島送り」と判定する。フリックは短時間なので、ページ送りに乗る
// 残留回転はわずか（許容）。
//
// 描画・DOM・乱数に触れない純関数。テストで境界（速い横フリック→page / ゆっくり横ドラッグ
// →rotate / 縦フリック→rotate）を担保する。

/**
 * フリック（島送り）と判定する離す瞬間の水平速度のしきい値（px/ms）。
 *
 * 目安（px/ms）:
 *   - 素早い指の弾き（フリック）: 例えば 120px を 100ms で弾けば vx = 1.2 px/ms。
 *     軽い弾きでも ~0.6〜1.0 px/ms 程度は出る。
 *   - ゆっくり眺め回すドラッグ: 同じ 120px を 600ms かけると vx = 0.2 px/ms、
 *     1 秒かければ 0.12 px/ms。持続的にゆっくり動かす限りここには届かない。
 * その境目として 0.5 px/ms を採る（弾けば超え、眺め回しは超えない明確な値）。
 */
export const FLICK_VX = 0.5;

/** フリックと認める合計水平移動量の下限（px）。これ未満の微小な弾きは無視（誤爆防止）。 */
export const FLICK_MIN_DX = 24;

/** 水平優位の係数（|dx| > |dy| * H_DOM）。縦に流れたフリックを島送りにしない。 */
export const H_DOM = 1.5;

/** ジェスチャ判定の結果。 */
export type GestureResult =
  | { kind: 'page'; direction: 1 | -1 } // 島送り（+1 = 次の島へ＝左フリック、-1 = 前の島へ＝右フリック）
  | { kind: 'rotate' }; // 島内回転（ドラッグ既定。フリックでなければ常にこれ）

/**
 * 指を離す瞬間のジェスチャ種別を、移動量と所要時間から判定する純関数（v0.7.3）。
 *
 * フリック（島送り）の条件:
 *   1. 水平速度 |vx| = |dx| / dtMs が FLICK_VX 以上（＝素早い弾き）。
 *   2. 水平優位（|dx| > |dy| * H_DOM）。
 *   3. 合計水平移動量 |dx| が FLICK_MIN_DX 以上（微小な弾きを無視）。
 * すべて満たすときだけ page。方向は dx の符号で決める（カルーセル的直感に合わせる）:
 *   左フリック（dx < 0）＝次の島（direction +1）、右フリック（dx > 0）＝前の島（direction -1）。
 * それ以外（遅い／縦優位／移動が小さい）は rotate（＝ドラッグは回転だっただけ。ページしない）。
 *
 * @param dx    水平移動量（始点→終点、右が正、px）。フリック判定では「離す瞬間付近」の dx を渡す。
 * @param dy    垂直移動量（下が正、px）。
 * @param dtMs  その移動にかかった時間（ms）。0 以下は速度無限大を避けるため rotate に倒す。
 */
export function classifyGesture(dx: number, dy: number, dtMs: number): GestureResult {
  const ax = Math.abs(dx);
  const ay = Math.abs(dy);
  if (dtMs <= 0) return { kind: 'rotate' }; // 時間ゼロ＝速度を出せない → 回転扱い
  if (ax < FLICK_MIN_DX) return { kind: 'rotate' }; // 弾きが小さすぎる
  if (ax <= ay * H_DOM) return { kind: 'rotate' }; // 横優位でない（縦/斜めフリック）
  const vx = ax / dtMs; // px/ms
  if (vx < FLICK_VX) return { kind: 'rotate' }; // 遅い＝ドラッグ（眺め回し）
  return { kind: 'page', direction: dx < 0 ? 1 : -1 };
}

// =========================================================================
// ピンチ（2 本指）の判定（v0.7.4）。純関数。
// =========================================================================
//
// 2 本指の操作は「ピンチズーム（カメラ拡縮）」に充てる（1 本指＝回転/フリック島送りと調停）。
// さらに **素早いピンチイン（一気に縮める）** を「島選択画面を開く」導線にする。
//
// ゆっくりしたピンチはズームに留め（画面遷移しない）、速い縮めだけ島選択へ飛ばすため、
// 指 2 本の距離の縮小速度（px/ms）を測ってしきい値で判定する。

/**
 * 「素早いピンチイン → 島選択画面」と判定する距離の縮小速度のしきい値（px/ms）。
 *
 * 目安: 2 本指の間隔を一気にすぼめる（例: 300px を 200ms で 60px まで＝240px 縮小／200ms
 * ＝ 1.2 px/ms）と十分超える。ゆっくり寄せる通常のズーム（300px を 1.5 秒で縮める＝
 * 0.16 px/ms 程度）は超えない。境目として 1.0 px/ms を採る（体感で調整した値。一気に
 * すぼめる動作だけが島選択に飛び、普通のズーム操作は飛ばない）。
 */
export const PINCH_FAST_VIN = 1.0;

/** ピンチインと認める最小の総縮小量（px）。これ未満の微小な縮みは無視（誤爆防止）。 */
export const PINCH_MIN_SHRINK = 80;

/**
 * 素早いピンチイン（島選択画面を開くべき操作）かを判定する純関数（v0.7.4）。
 *
 * @param startDist  ピンチ開始時の 2 指間距離（px）。
 * @param endDist    現在（または離す瞬間）の 2 指間距離（px）。
 * @param dtMs       その変化にかかった時間（ms）。
 * @returns true なら「素早いピンチイン」＝島選択画面を開く。条件:
 *   1. 縮んでいる（endDist < startDist）。
 *   2. 総縮小量 (startDist - endDist) が PINCH_MIN_SHRINK 以上（微小な縮みを無視）。
 *   3. 縮小速度 (startDist - endDist) / dtMs が PINCH_FAST_VIN 以上（一気にすぼめた）。
 *   ゆっくりしたピンチ（普通のズーム）は 3 を満たさず false（画面遷移しない）。
 */
export function isFastPinchIn(startDist: number, endDist: number, dtMs: number): boolean {
  if (dtMs <= 0) return false;
  const shrink = startDist - endDist;
  if (shrink < PINCH_MIN_SHRINK) return false; // 縮んでいない or 微小
  const vin = shrink / dtMs; // px/ms
  return vin >= PINCH_FAST_VIN;
}
